import type { ContentfulExperimentFields } from '@devtinker/shared';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

// Dynamically import experiment components
const experimentComponents: Record<string, React.LazyExoticComponent<React.FC>> = {
  'audio-viz': lazy(() => import('../experiments/audio-viz')),
  'fractal-explorer': lazy(() => import('../experiments/fractal-explorer')),
  'glsl-art': lazy(() => import('../experiments/glsl-art')),
  'web-apis': lazy(() => import('../experiments/web-apis')),
  'hello-world': lazy(() => import('../experiments/hello-world')),
  'clock': lazy(() => import('../experiments/clock')),
  'flow-field': lazy(() => import('../experiments/flow-field')),
  'physics-sandbox': lazy(() => import('../experiments/physics-sandbox')),
  'orb-simple': lazy(() => import('../experiments/orb-simple')),
  // Add more experiments here as they are created
};

function getMetadataStatusPresentation(status: ContentfulExperimentFields['status']) {
  if (status === 'active') {
    return {
      className: 'bg-green-100 text-green-700',
      label: 'Active'
    };
  }

  if (status === 'coming-soon') {
    return {
      className: 'bg-yellow-100 text-yellow-700',
      label: 'Coming Soon'
    };
  }

  return {
    className: 'bg-gray-100 text-gray-700',
    label: 'Archived'
  };
}

const ExperimentDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ContentfulExperimentFields | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);

  useEffect(() => {
    // Check if experiment exists
    if (slug && !experimentComponents[slug]) {
      setError(`Experiment "${slug}" not found`);
    } else {
      setError(null);
    }

    // Fetch metadata from Contentful
    const fetchMetadata = async () => {
      if (!slug) return;

      const spaceId = import.meta.env.VITE_CONTENTFUL_SPACE_ID;
      const accessToken = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

      if (!spaceId || !accessToken) {
        console.log('No Contentful credentials, skipping metadata fetch');
        setIsLoadingMetadata(false);
        return;
      }

      try {
        const response = await fetch(
          `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?access_token=${accessToken}&content_type=experiment&fields.slug=${slug}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.items && data.items.length > 0) {
            setMetadata(data.items[0].fields);
            console.log('Loaded experiment metadata:', data.items[0].fields);
          }
        }
      } catch (error) {
        console.error('Error fetching experiment metadata:', error);
      } finally {
        setIsLoadingMetadata(false);
      }
    };

    fetchMetadata();
  }, [slug]);

  if (!slug) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">No Experiment Specified</h1>
        <p className="text-gray-600 mb-8">
          Please provide an experiment slug in the URL.
        </p>
        <Link
          to="/"
          className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Experiments
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Experiment Not Found</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-8">
            The experiment "{slug}" hasn't been created yet or the slug is incorrect.
          </p>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left max-w-2xl mx-auto">
          <h3 className="text-lg font-bold text-blue-900 mb-2">💡 Creating a New Experiment</h3>
          <ol className="list-decimal list-inside text-blue-800 space-y-2">
            <li>Create a folder: <code className="bg-blue-100 px-2 py-1 rounded">src/experiments/{slug}/</code></li>
            <li>Add an <code className="bg-blue-100 px-2 py-1 rounded">index.tsx</code> file with your React component</li>
            <li>Register it in <code className="bg-blue-100 px-2 py-1 rounded">ExperimentDetail.tsx</code></li>
            <li>Create a matching Contentful entry with this slug</li>
          </ol>
        </div>

        <Link
          to="/"
          className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Experiments
        </Link>
      </div>
    );
  }

  const ExperimentComponent = experimentComponents[slug];
  const metadataStatus = metadata?.status ? getMetadataStatusPresentation(metadata.status) : null;

  return (
    <div>
      {/* Back link */}
      <div className="mb-8">
        <Link
          to="/"
          className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Experiments
        </Link>
      </div>

      {/* Metadata section */}
      {metadata && !isLoadingMetadata && (
        <div className="mb-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{metadata.title}</h1>
          <p className="text-gray-600 mb-4">{metadata.description}</p>
          {metadata.technologies && metadata.technologies.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {metadata.technologies.map((tech) => (
                <span key={tech} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  {tech}
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-4 text-sm text-gray-600">
            {metadataStatus && (
              <span className={`px-3 py-1 rounded-full font-medium ${metadataStatus.className}`}>
                {metadataStatus.label}
              </span>
            )}
            {metadata.sourceCodeUrl && (
              <a 
                href={metadata.sourceCodeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
                View Source
              </a>
            )}
          </div>
        </div>
      )}

      {/* Load the experiment component */}
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading experiment...</p>
            </div>
          </div>
        }
      >
        <ExperimentComponent />
      </Suspense>
    </div>
  );
};

export default ExperimentDetail;
